// Modules
import React, { useEffect, useRef, useState } from 'react'
import { Button, FormGroup, InputGroup, NonIdealState } from '@blueprintjs/core'
import { useNavigate } from 'react-router-dom'

// Components
import Logo from 'components/brand/logo/Logo'
import Page from 'components/layout/page/Page'

// Hooks
import useLanguage from 'hooks/system/useLanguage'
import useNotices from 'hooks/system/useNotices'
import useTwoFactorAuthentication from 'hooks/authentication/useTwoFactorAuthentication'

// Constants
import { TWO_FACTOR_AUTHENTICATION_CODE_LENGTH } from 'constants/settings'

// Styles
import './Form.scss'

// Section: Two-factor authentication > Landing > Form
function Form () {
	// References
	const form = useRef()

	// State
	const [formData, setFormData] = useState({ code: '' })
	const [qrCodeData, setQRCodeData] = useState(null)
	const [isInitialized, setIsInitialized] = useState(false)
	const [hasError, setHasError] = useState(false)

	// Data
	const navigate = useNavigate()
	const { addNotice } = useNotices()
	const { enable: enableTwoFactorAuthentication, getQRCode, isProcessing } = useTwoFactorAuthentication()
	const { translate } = useLanguage('pages.twoFactorAuthentication.views.landing.sections.form')
	const isFormReady = formData.code.length === TWO_FACTOR_AUTHENTICATION_CODE_LENGTH

	// Functions
	function handleInput (type, value) {
		setFormData(formData => ({ ...formData, [type]: value }))
	}
	async function handleSubmit (event) {
		event.preventDefault()
		if (isFormReady) {
			const { hasError: hasEnablingError } = await enableTwoFactorAuthentication(formData.code)
			if (!hasEnablingError) navigate('/')
			else setHasError(true)
		}
	}

	// Effects
	// TODO: If the 2FA is already enabled for the user, navigate (BE help needed)
	useEffect(() => {
		setTimeout(() => setIsInitialized(true), 1000)
	}, [])
	useEffect(() => {
		async function setQRCode () {
			const { data, hasError: hasQRCodeError } = await getQRCode()
			if (hasQRCodeError) setHasError(true)
			else if (!!data) setQRCodeData(data)
		}
		if (isInitialized) setQRCode()
	}, [addNotice, getQRCode, isInitialized])

	// Render
	return (
		<Page.Section
			willGrow
			willCenter
			name="form">
			<div className={
				'aix-authentication-form' +
				(isInitialized ? ' aix-initialized' : '')
			}>
				<Logo className="aix-logo" />
				<div className="aix-content">
					<form
						ref={form}
						className="aix-form aix-paper-thick">
						<h2 className="aix-title">{translate.local('form.title')}</h2>
						{!!qrCodeData && !hasError &&
							<>
								<div className="aix-visual">
									<img
										className="aix-image"
										src={qrCodeData} />
								</div>
								<FormGroup>
									<InputGroup
										disabled={isProcessing}
										name="code"
										type="text"
										value={formData.code}
										className="aix-input aix-code"
										placeholder={translate.local('form.fields.code.placeholder')}
										onChange={({ target }) => handleInput('code', target.value)} />
								</FormGroup>
								<Button
									fill
									disabled={!isFormReady || isProcessing || !qrCodeData || hasError}
									loading={isProcessing}
									className="aix-submit bp4-large"
									onClick={handleSubmit}
									type="submit">
									{translate.local('form.actions.activate')}
								</Button>
								{/*isOptional &&
									<Button
										fill
										className="aix-skip bp4-large"
										onClick={() => {}}>
										{translate.local('form.actions.skip')}
									</Button>
								*/}
							</>
						}
						{hasError &&
							<NonIdealState
								// icon="path-search"
								description={translate.local('form.fallback.message')}
								action={
									<Button
										large
										icon="refresh"
										style={{ marginTop: '2rem' }}
										onClick={() => window.location.reload()}>
										{translate.local('form.actions.reload')}
									</Button>
								} />
						}
					</form>
				</div>
			</div>
		</Page.Section>
	)
}

// Export
export default Form
